// 고객 주소 코드(다음 우편번호 검색 결과) — 형식은 `0063` 의 check 와 같은 값이다.

import { z } from "zod";

import { CustomerAddress } from "./types";

export const ADDRESS_FIELDS = [
  "address_road",
  "address_jibun",
  "address_zonecode",
  "address_bcode",
  "address_building_code",
  "address_detail",
] as const satisfies readonly (keyof CustomerAddress)[];

export type AddressField = (typeof ADDRESS_FIELDS)[number];

export const EMPTY_ADDRESS: CustomerAddress = {
  address_road: null,
  address_jibun: null,
  address_zonecode: null,
  address_bcode: null,
  address_building_code: null,
  address_detail: null,
};

export const ADDRESS_BASE_MAX = 120;
export const ADDRESS_DETAIL_MAX = 60;
export const ZONECODE_RE = /^[0-9]{5}$/;
export const BCODE_RE = /^[0-9]{10}$/;
export const BUILDING_CODE_RE = /^[0-9]{25}$/;

// 코드·검색 주소는 검색 결과를 옮긴 부가 정보라 형식이 틀리면(손댄 요청·다음 쪽 형식 변경) 그 칸만 버리고 저장은 막지 않는다 — 주소 글자가 더 중요하다.
const looseCode = (re: RegExp) =>
  z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v && re.test(v) ? v : null));

const looseBase = z
  .string()
  .trim()
  .nullable()
  .optional()
  .transform((v) => (v && v.length <= ADDRESS_BASE_MAX ? v : null));

export const ADDRESS_SHAPE = {
  address_road: looseBase,
  address_jibun: looseBase,
  address_zonecode: looseCode(ZONECODE_RE),
  address_bcode: looseCode(BCODE_RE),
  address_building_code: looseCode(BUILDING_CODE_RE),
  // 상세주소는 사람이 친 글자라 버리지 않고 되묻는다.
  address_detail: z
    .string()
    .trim()
    .max(ADDRESS_DETAIL_MAX, `상세주소는 ${ADDRESS_DETAIL_MAX}자 이내로 적어 주세요`)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
};

const addressSchema = z.object(ADDRESS_SHAPE);

export type AddressGroup = CustomerAddress & { site_address: string };

export type AddressGroupResult = { ok: true; value: AddressGroup } | { ok: false; message: string };

// 검색 주소가 있으면 site_address 를 「도로명(없으면 지번) + 상세」 로 다시 짓고, 없으면 친 글자 그대로 · 코드는 전부 비운다.
export const composeSiteAddress = (base: string, detail: string | null): string =>
  [base, detail].filter((v): v is string => Boolean(v)).join(" ");

export const readAddressGroup = (
  typedSite: string,
  raw: Partial<Record<AddressField, unknown>>
): AddressGroupResult => {
  const parsed = addressSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, message: parsed.error.issues[0]?.message ?? "주소를 확인해 주세요" };
  const picked: CustomerAddress = { ...EMPTY_ADDRESS, ...parsed.data };
  const base = picked.address_road ?? picked.address_jibun;
  if (!base) return { ok: true, value: { ...EMPTY_ADDRESS, site_address: typedSite } };
  return { ok: true, value: { ...picked, site_address: composeSiteAddress(base, picked.address_detail) } };
};

export type AddressPatchResult = { ok: true; patch: Record<string, unknown> } | { ok: false; message: string };

// 주소 칸은 묶음으로만 받는다 — site_address 만 오면 손 입력(코드 비움), 일부만 오면 통째로 무시해 저장된 주소·코드를 지킨다.
export const withAddressForm = (patch: Record<string, unknown>, form: FormData): AddressPatchResult => {
  const present = ADDRESS_FIELDS.filter((key) => form.has(key));
  const hasSite = form.has("site_address");
  if (!hasSite && present.length === 0) return { ok: true, patch };
  if (!hasSite || (present.length > 0 && present.length < ADDRESS_FIELDS.length)) {
    const kept = { ...patch };
    delete kept.site_address;
    return { ok: true, patch: kept };
  }
  const get = (key: string): string => {
    const value = form.get(key);
    return typeof value === "string" ? value.trim() : "";
  };
  const read = readAddressGroup(
    get("site_address"),
    Object.fromEntries(present.map((key) => [key, get(key)])) as Partial<Record<AddressField, string>>
  );
  return read.ok ? { ok: true, patch: { ...patch, ...read.value } } : read;
};
